// Pure, framework-free operations on a PostComposition. The editor hook wraps these
// with React state + undo/redo; SelectionOverlay uses the geometry ops. Keeping them
// pure makes the editor's brain unit-testable without a DOM.

import type {
  AspectRatio,
  DecorLayer,
  DecorLayerMark,
  ImageLayer,
  Layer,
  PostComposition,
  ShapeLayer,
  TextLayer,
  TextRun,
} from '../types'
import { decorBox } from '../decor'
import { groupRunsIntoLines } from '../render-helpers'

/** The brand pen every editor affordance seeds with (underline/highlight/decor/shape). */
export function brandAccent(comp: PostComposition): string {
  return comp.brand.palette.accent ?? comp.brand.palette.primary
}

export function clone<T>(v: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(v)
    : (JSON.parse(JSON.stringify(v)) as T)
}

export function withDirty(comp: PostComposition): PostComposition {
  return comp.meta.dirty ? comp : { ...comp, meta: { ...comp.meta, dirty: true } }
}

export function mapLayer(
  comp: PostComposition,
  id: string,
  patch: (l: Layer) => Layer
): PostComposition {
  return { ...comp, layers: comp.layers.map(l => (l.id === id ? patch(l) : l)) }
}

export function maxZ(comp: PostComposition): number {
  return comp.layers.reduce((m, l) => Math.max(m, l.z), 0)
}

let idSeq = 0
export function newLayerId(kind: string): string {
  idSeq = (idSeq + 1) % 1_000_000
  return `usr_${kind}_${Date.now().toString(36)}_${idSeq.toString(36)}`
}

export function addLayer(comp: PostComposition, layer: Layer): PostComposition {
  return { ...comp, layers: [...comp.layers, layer] }
}

export function removeLayer(comp: PostComposition, id: string): PostComposition {
  return { ...comp, layers: comp.layers.filter(l => l.id !== id) }
}

export function makeTextLayer(comp: PostComposition, id: string): TextLayer {
  return {
    id,
    type: 'text',
    name: 'Text',
    userEdited: true,
    z: maxZ(comp) + 1,
    x: 0.1,
    y: 0.44,
    w: 0.8,
    h: 0.12,
    runs: [{ text: 'New text' }],
    fontFamily: 'Display',
    fontWeight: 400,
    fontSizePct: 0.06,
    color: comp.brand.palette.neutralLight ?? '#FAFAF7',
    align: 'left',
    lineHeight: 1.1,
    direction: 'ltr',
  }
}

export function makeShapeLayer(comp: PostComposition, id: string): ShapeLayer {
  return {
    id,
    type: 'shape',
    name: 'Shape',
    userEdited: true,
    z: maxZ(comp) + 1,
    x: 0.1,
    y: 0.1,
    w: 0.3,
    h: 0.08,
    shapeKind: 'rect',
    fill: { kind: 'solid', color: brandAccent(comp) },
    radiusPct: 0.1,
  }
}

// A user-added photo layer: centered, ~40% of the canvas, cover-fit, lightly rounded. The src is
// the stable {kind:'attachment'} id (re-signed server-side at render; resolved to a display url on
// the client) and srcUserChosen so an AI revise keeps it. Drops on top (maxZ+1).
export function makeImageLayer(
  comp: PostComposition,
  id: string,
  attachmentId: string
): ImageLayer {
  return {
    id,
    type: 'image',
    name: 'Photo',
    userEdited: true,
    z: maxZ(comp) + 1,
    x: 0.3,
    y: 0.34,
    w: 0.4,
    h: 0.32,
    src: { kind: 'attachment', attachmentId },
    srcUserChosen: true,
    objectFit: 'cover',
    radiusPct: 0.02,
  }
}

// A user-added accent mark: seeded in the brand accent at the mark's natural size on THIS
// canvas (worker decorBox math — aspect-correct on 1:1/9:16, not just 4:5), dropped just
// below center so it lands near the headline zone. Declarative (mark + color), so the
// toolbar/inspector can re-mark and recolor it crisply.
export function makeDecorLayer(
  comp: PostComposition,
  id: string,
  mark: DecorLayerMark
): DecorLayer {
  const px = decorBox(mark, comp.canvas.width, comp.canvas.height)
  const w = px.w / comp.canvas.width
  return {
    id,
    type: 'decor',
    name: 'Accent mark',
    userEdited: true,
    z: maxZ(comp) + 1,
    x: (1 - w) / 2,
    y: 0.58,
    w,
    h: px.h / comp.canvas.height,
    mark,
    color: brandAccent(comp),
  }
}

export function duplicateLayer(
  comp: PostComposition,
  id: string
): { comp: PostComposition; newId: string | null } {
  const src = comp.layers.find(l => l.id === id)
  if (!src) return { comp, newId: null }
  const newId = newLayerId(src.type)
  const copy = {
    ...clone(src),
    id: newId,
    z: maxZ(comp) + 1,
    x: Math.min(0.92, src.x + 0.03),
    y: Math.min(0.92, src.y + 0.03),
    seeded: false,
    userEdited: true,
    role: undefined,
    name: src.name ? `${src.name} copy` : undefined,
  } as Layer
  return { comp: addLayer(comp, copy), newId }
}

// --- text style (run-aware) ----------------------------------------------
// A seeded headline is multi-run (lead/focus/trail each carry their own color / size /
// weight / family), and the renderer resolves `run.X ?? layer.X` in BOTH the client canvas
// and the worker master. So a whole-layer style change must be written to the runs too, or
// it's silently swallowed and nothing changes on screen.

const TEXT_SIZE_MIN = 0.012
const TEXT_SIZE_MAX = 0.3
export const clampTextSize = (v: number): number =>
  Math.max(TEXT_SIZE_MIN, Math.min(TEXT_SIZE_MAX, v))

/** Set a whole-layer text style (color/weight/family) on the layer AND every run. */
export function restyleText(
  layer: TextLayer,
  patch: { color?: string; fontWeight?: number; fontFamily?: string }
): TextLayer {
  return {
    ...layer,
    ...patch,
    runs: layer.runs.map(r => ({ ...r, ...patch })),
    userEdited: true,
  }
}

/** Scale the whole headline by a factor, preserving each run's emphasis ratio. */
export function scaleText(layer: TextLayer, factor: number): TextLayer {
  return {
    ...layer,
    fontSizePct: clampTextSize(layer.fontSizePct * factor),
    runs: layer.runs.map(r =>
      r.fontSizePct != null ? { ...r, fontSizePct: clampTextSize(r.fontSizePct * factor) } : r
    ),
    userEdited: true,
  }
}

/** Set an absolute nominal layer size; scales the runs proportionally so it's visible. */
export function resizeTextTo(layer: TextLayer, layerPct: number): TextLayer {
  const target = clampTextSize(layerPct)
  const factor = layer.fontSizePct > 0 ? target / layer.fontSizePct : 1
  return {
    ...layer,
    fontSizePct: target,
    runs: layer.runs.map(r =>
      r.fontSizePct != null ? { ...r, fontSizePct: clampTextSize(r.fontSizePct * factor) } : r
    ),
    userEdited: true,
  }
}

/**
 * Rewrite a text layer's words WITHOUT discarding its per-line styles. The incoming text is
 * split by newline into lines. A line whose text is UNCHANGED keeps its original style even if
 * its position shifted (matched by content), so inserting/removing a line no longer drags every
 * following line's color/size onto the wrong row. A line with no content match (a typo edit or a
 * brand-new line) falls back to the existing line at the same index, then the last line. Intra-line
 * inline emphasis collapses to the line's leading run (a plain-text edit can't express it; the rich
 * editor preserves spans). Each result run starts its own line (no inline flag).
 */
export function setTextPreservingRuns(layer: TextLayer, text: string): TextLayer {
  const existingLines = groupRunsIntoLines(layer.runs)
  const lineText = (line: TextRun[]): string => line.map(r => r.text).join('')
  // Style (leading run) keyed by the line's full text; first occurrence wins on duplicates.
  const styleByText = new Map<string, TextRun | undefined>()
  for (const line of existingLines) {
    const key = lineText(line)
    if (!styleByText.has(key)) styleByText.set(key, line[0])
  }

  const lines = text.split('\n')
  const runs: TextRun[] = lines.map((t, i) => {
    const src = styleByText.has(t)
      ? styleByText.get(t)
      : (existingLines[i] ?? existingLines[existingLines.length - 1])?.[0]
    const run: TextRun = { text: t }
    if (src) {
      if (src.color != null) run.color = src.color
      if (src.fontSizePct != null) run.fontSizePct = src.fontSizePct
      if (src.fontWeight != null) run.fontWeight = src.fontWeight
      if (src.fontFamily != null) run.fontFamily = src.fontFamily
      if (src.italic) run.italic = true
      if (src.underlineColor != null) run.underlineColor = src.underlineColor
      if (src.highlightColor != null) run.highlightColor = src.highlightColor
      if (src.encircleColor != null) run.encircleColor = src.encircleColor
    }
    return run
  })
  return { ...layer, runs: runs.length ? runs : [{ text: '' }], userEdited: true }
}

// --- rich-text range styling (select a span, restyle just it) --------------
// The editable text is the runs' text with '\n' between LINES (see groupRunsIntoLines). A
// global range [start,end) addresses characters; we split runs at the boundaries, transform the
// covered runs, and merge adjacent runs that end up identical. Inline flags are recomputed on
// flatten (first run of each line starts the line; the rest flow inline).

function runStyleKey(r: TextRun): string {
  return [
    r.color ?? '',
    r.fontSizePct ?? '',
    r.fontWeight ?? '',
    r.fontFamily ?? '',
    r.italic ? 1 : '',
    r.underlineColor ?? '',
    r.highlightColor ?? '',
    r.encircleColor ?? '',
  ].join('|')
}

/** Merge adjacent runs WITHIN one line that share the same visual style (keeps the doc tidy). */
export function mergeAdjacentRuns(line: TextRun[]): TextRun[] {
  const out: TextRun[] = []
  for (const r of line) {
    const prev = out[out.length - 1]
    if (prev && runStyleKey(prev) === runStyleKey(r)) prev.text += r.text
    else out.push({ ...r })
  }
  return out
}

/** Split a line's runs so a run boundary exists at the line-local char offset. */
function splitLineAt(line: TextRun[], offset: number): TextRun[] {
  const out: TextRun[] = []
  let pos = 0
  for (const r of line) {
    const len = r.text.length
    if (offset > pos && offset < pos + len) {
      const cut = offset - pos
      out.push({ ...r, text: r.text.slice(0, cut) })
      out.push({ ...r, text: r.text.slice(cut) })
    } else out.push({ ...r })
    pos += len
  }
  return out
}

function mapLineRange(
  line: TextRun[],
  start: number,
  end: number,
  fn: (r: TextRun) => TextRun
): TextRun[] {
  if (end <= start) return line
  let r = splitLineAt(line, start)
  r = splitLineAt(r, end)
  let pos = 0
  r = r.map(run => {
    const s = pos
    const e = pos + run.text.length
    pos = e
    return e > s && s >= start && e <= end ? fn(run) : run
  })
  return mergeAdjacentRuns(r)
}

function lineStartRun(r: TextRun): TextRun {
  const c = { ...r }
  delete c.inline
  return c
}

function flattenLines(lines: TextRun[][]): TextRun[] {
  const runs: TextRun[] = []
  for (const line of lines) {
    line.forEach((r, i) => runs.push(i > 0 ? { ...r, inline: true } : lineStartRun(r)))
  }
  return runs
}

/** Apply fn to every run covered by the GLOBAL range [start,end) across the layer's lines. */
export function mapRunRange(
  layer: TextLayer,
  start: number,
  end: number,
  fn: (r: TextRun) => TextRun
): TextLayer {
  const lines = groupRunsIntoLines(layer.runs)
  let pos = 0
  const newLines = lines.map(line => {
    const len = line.reduce((n, r) => n + r.text.length, 0)
    const ls = Math.max(0, start - pos)
    const le = Math.min(len, end - pos)
    pos += len + 1 // +1 for the '\n' between lines
    return le > ls ? mapLineRange(line, ls, le, fn) : line
  })
  return { ...layer, runs: flattenLines(newLines), userEdited: true }
}

/** The runs overlapping the GLOBAL range [start,end). Lets the toolbar read the selection's
 *  current style (e.g. "is the selection bold") instead of guessing from runs[0]. */
export function runsInRange(layer: TextLayer, start: number, end: number): TextRun[] {
  const covered: TextRun[] = []
  let pos = 0
  for (const line of groupRunsIntoLines(layer.runs)) {
    const len = line.reduce((n, r) => n + r.text.length, 0)
    const ls = Math.max(0, start - pos)
    const le = Math.min(len, end - pos)
    if (le > ls) {
      let p = 0
      for (const r of line) {
        const s = p
        const e = p + r.text.length
        if (e > ls && s < le && r.text.length) covered.push(r)
        p = e
      }
    }
    pos += len + 1
  }
  return covered
}

type RunStylePatch = {
  color?: string
  fontSizePct?: number
  fontWeight?: number
  fontFamily?: string
  // Treatments: `false`/`null` REMOVES the field (a spread would leave a dead key behind and
  // the run would never merge back with its plain neighbors).
  italic?: boolean
  underlineColor?: string | null
  highlightColor?: string | null
  encircleColor?: string | null
}

function applyRunPatch(r: TextRun, patch: RunStylePatch): TextRun {
  const c: TextRun = { ...r }
  if (patch.color != null) c.color = patch.color
  if (patch.fontSizePct != null) c.fontSizePct = patch.fontSizePct
  if (patch.fontWeight != null) c.fontWeight = patch.fontWeight
  if (patch.fontFamily != null) c.fontFamily = patch.fontFamily
  if ('italic' in patch) {
    if (patch.italic) c.italic = true
    else delete c.italic
  }
  for (const k of ['underlineColor', 'highlightColor', 'encircleColor'] as const) {
    if (k in patch) {
      const v = patch[k]
      if (v) c[k] = v
      else delete c[k]
    }
  }
  return c
}

/** Set a style on just the selected characters (color/weight/family/size/treatments). */
export function restyleRange(
  layer: TextLayer,
  start: number,
  end: number,
  patch: RunStylePatch
): TextLayer {
  return mapRunRange(layer, start, end, r => applyRunPatch(r, patch))
}

/** Set a treatment (italic/underline/highlight/encircle) on the layer AND every run. */
export function restyleTreatment(layer: TextLayer, patch: RunStylePatch): TextLayer {
  return { ...layer, runs: layer.runs.map(r => applyRunPatch(r, patch)), userEdited: true }
}

/** Scale only the selected characters' size by a factor (A+/A− on a selection). */
export function scaleRange(
  layer: TextLayer,
  start: number,
  end: number,
  factor: number,
  layerBasePct: number
): TextLayer {
  return mapRunRange(layer, start, end, r => ({
    ...r,
    fontSizePct: clampTextSize((r.fontSizePct ?? layerBasePct) * factor),
  }))
}

/** Set a text layer's runs directly (the rich inline editor serializes its DOM → runs). */
export function setRuns(layer: TextLayer, runs: TextRun[]): TextLayer {
  return { ...layer, runs: runs.length ? runs : [{ text: '' }], userEdited: true }
}

export type ReorderDir = 'front' | 'back' | 'forward' | 'backward'

export function reorderLayers(comp: PostComposition, id: string, dir: ReorderDir): PostComposition {
  const sorted = [...comp.layers].sort((a, b) => a.z - b.z)
  const idx = sorted.findIndex(l => l.id === id)
  if (idx < 0) return comp
  let target = idx
  if (dir === 'front') target = sorted.length - 1
  else if (dir === 'back') target = 0
  else if (dir === 'forward') target = Math.min(sorted.length - 1, idx + 1)
  else target = Math.max(0, idx - 1)
  if (target === idx) return comp
  const [moved] = sorted.splice(idx, 1)
  sorted.splice(target, 0, moved!)
  return { ...comp, layers: sorted.map((l, i) => ({ ...l, z: i })) }
}

/**
 * Drag-to-reorder: move layer `id` to just BEFORE `beforeId` in the TOP-DOWN list (index 0 =
 * front-most / highest z, the order the Layers panel shows), or to the end when `beforeId` is
 * null. Renormalizes z so the new stacking persists. Unambiguous for drag-drop (no off-by-one);
 * a no-op move returns the same comp.
 */
export function moveLayerBefore(
  comp: PostComposition,
  id: string,
  beforeId: string | null
): PostComposition {
  const topDown = [...comp.layers].sort((a, b) => b.z - a.z)
  const moving = topDown.find(l => l.id === id)
  if (!moving || id === beforeId) return comp
  const rest = topDown.filter(l => l.id !== id)
  const at = beforeId == null ? rest.length : rest.findIndex(l => l.id === beforeId)
  rest.splice(at < 0 ? rest.length : at, 0, moving)
  const n = rest.length
  // top-down index 0 => highest z.
  const next = rest.map((l, i) => ({ ...l, z: n - 1 - i }))
  // No-op guard: if order is unchanged, return original.
  if (next.every((l, i) => l.id === topDown[i]!.id)) return comp
  return { ...comp, layers: next }
}

const ASPECT_PX: Record<AspectRatio, { w: number; h: number }> = {
  '4:5': { w: 1080, h: 1350 },
  '9:16': { w: 1080, h: 1920 },
  '1:1': { w: 1080, h: 1080 },
  '2:3': { w: 1080, h: 1620 },
}

export function setAspect(comp: PostComposition, aspect: AspectRatio): PostComposition {
  const dims = ASPECT_PX[aspect]
  return { ...comp, canvas: { ...comp.canvas, aspectRatio: aspect, width: dims.w, height: dims.h } }
}

// --- geometry (shared with SelectionOverlay) -----------------------------

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

export function clampNorm(v: number): number {
  return Math.max(-0.25, Math.min(1.25, v))
}

export function applyResize(
  handle: ResizeHandle,
  box: { x: number; y: number; w: number; h: number },
  dx: number,
  dy: number
): { x: number; y: number; w: number; h: number } {
  let { x, y, w, h } = box
  const MIN = 0.02
  if (handle.includes('e')) w = Math.max(MIN, box.w + dx)
  if (handle.includes('s')) h = Math.max(MIN, box.h + dy)
  if (handle.includes('w')) {
    w = Math.max(MIN, box.w - dx)
    x = box.x + (box.w - w)
  }
  if (handle.includes('n')) {
    h = Math.max(MIN, box.h - dy)
    y = box.y + (box.h - h)
  }
  return { x, y, w, h }
}
